import './AccordionFAQ.css';
import React, { useCallback } from 'react';
import { get } from '../../utils';
import RouterLink from '../atoms/RouterLink';
import { RichText, Text, Link as SitecoreLink } from '@sitecore-jss/sitecore-jss-react';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import IconPlus from '../atoms/Icons/IconPlus';
import IconMinus from '../atoms/Icons/IconMinus';
import { ReactComponent as IconChevronDown } from '../../resources/icons/arrowDownC.svg';
import { ReactComponent as IconExpandPlus } from '../../resources/icons/black-plus.svg';
import { ReactComponent as IconMinuss } from '../../resources/icons/accordion-minus.svg';
import { tealiumMergeClickData, tealiumClickFunc } from '../../utils/tealium';
import { convertToAnalyticObject } from '../../utils/analytics';
import { isNil } from '../../utils/IsNil';
export const AccordionFAQ = _ref => {
  let {
    fields,
    params
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const oneColumn = params !== undefined && params['OneColumn'] === '1' ? true : false;
  const [activeItemsIds, setActiveItemsIds] = React.useState([]);
  const isDetSite = get(params, 'RenderingStyle') === 'DDA';
  const titleField = get(fields, 'data.item.title.field');
  const titleFieldValue = String(titleField ? titleField.value || '' : '');
  const description = get(fields, 'data.item.description.field.value', '');
  const readAllLabel = get(fields, 'data.item.readAllLabel.field.value');
  const readAllLabelField = get(fields, 'data.item.readAllLabel');
  const readAllLinkField = get(fields, 'data.item.readAllLink.field');
  const href = get(fields, 'data.item.readAllLink.field.value.href', '/');
  const readAllLinkHref = href.endsWith('pdf') ? `${sitecoreContext.origin}/${href}` : href;
  const hasItems = Boolean(get(fields, 'data.item.items.targetItems.length'));
  const ctaLeftIcon = get(fields, 'data.item.ctaLeftIcon.targetItem');
  const ctaRightIcon = get(fields, 'data.item.ctaRightIcon.targetItem');
  const filteredItems = () => {
    const notFilteredItems = hasItems ? fields.data.item.items.targetItems : [];
    return notFilteredItems.filter(item => {
      const title = get(item, 'title.field.value');
      if (isNil(title) || title === "") {
        return false;
      }
      return item;
    });
  };
  const items = filteredItems();
  const middleArrIndex = hasItems ? Math.ceil(items.length / 2) : 0;
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const expandAllText = get(fields, 'data.item.expandAllText.field.value');
  const collapseAllText = get(fields, 'data.item.collapseAllText.field.value');
  const allItemsExpanded = pageEditing || items.length === activeItemsIds.length;
  React.useEffect(() => {
    const isFirstExpanded = Boolean(get(fields, 'data.item.isFirstExpanded.field.value'));
    if (pageEditing) {
      setActiveItemsIds(items.length ? items.map(i => i.id) : []);
    } else if (isFirstExpanded && hasItems) {
      setActiveItemsIds(items.length ? [items[0].id] : []);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccordionItemClick = React.useCallback((e, id, isActive) => {
    e.preventDefault();
    const nextActiveItemsIds = isActive ? activeItemsIds.filter(i => i !== id) : [...activeItemsIds, id];
    setActiveItemsIds(nextActiveItemsIds);
  }, [activeItemsIds]);
  const handleToggleAllItemsClick = useCallback(e => {
    e.preventDefault();
    const nextActiveItemsIds = allItemsExpanded ? [] : items.map(item => item.id);
    setActiveItemsIds(nextActiveItemsIds);
  }, [allItemsExpanded, items]);
  if (!fields || !fields.data || !fields.data.item) {
    console.info('AccordionFAQ component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", null, "[AccordionFAQ component is empty: please provide data source]");
    }
    return null;
  }
  const renderItem = (item, pageEditing, isDetSite) => {
    const isActive = pageEditing || activeItemsIds.includes(item.id);
    const titleField = get(item, 'title.field');
    const description = get(item, 'description.field');
    const name = get(item, 'name');
    const iconSvgCode = get(item, 'iconSvgCode.jss.value');
    const titleValue = titleField ? titleField.value : '';
    const descriptionValue = description ? description.value : '';
    if (titleValue && descriptionValue) {
      return /*#__PURE__*/React.createElement("div", {
        className: `accordion__list ${isActive ? "accordion__list--active" : ""}`,
        key: item.id
      }, /*#__PURE__*/React.createElement("p", {
        className: "accordion__heading body-medium",
        onClick: e => {
          if (!pageEditing) {
            handleAccordionItemClick(e, item.id, isActive);
            tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['questionAnalytics'], params), `${name}: ${isActive ? 'Collapse' : 'Expand'}`);
          }
        },
        onKeyPress: e => {
          if (e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            handleAccordionItemClick(e, item.id, isActive);
            tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['questionAnalytics'], params), `${name}: ${isActive ? 'Collapse' : 'Expand'}`);
          }
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "wrapIconAndTitle"
      }, iconSvgCode && /*#__PURE__*/React.createElement("div", {
        dangerouslySetInnerHTML: {
          __html: iconSvgCode
        },
        className: "title__icon"
      }), /*#__PURE__*/React.createElement(Text, {
        tag: pageEditing ? 'span' : 'a',
        className: "accordion__anchor",
        href: "/",
        field: titleField,
        "data-cy": "accordion-faq-anchor"
      })), !pageEditing && !isDetSite && /*#__PURE__*/React.createElement("span", {
        className: "accordion__icon"
      }, isActive ? /*#__PURE__*/React.createElement(IconMinus, null) : /*#__PURE__*/React.createElement(IconPlus, null)), !pageEditing && isDetSite && /*#__PURE__*/React.createElement("span", {
        className: "accordion__icon"
      }, /*#__PURE__*/React.createElement(IconChevronDown, {
        className: `${isActive ? "iconDown" : "iconUp"}`
      }))), isActive && /*#__PURE__*/React.createElement("div", {
        className: "accordion__content"
      }, /*#__PURE__*/React.createElement(RichText, {
        tag: "div",
        className: "body-small accordion__desc",
        field: description,
        "data-cy": "accordion-faq-item-content"
      })));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    anchorUrlParameter: params['Anchor Url Parameter'],
    isDetSite: isDetSite
  }, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: {
      __html: `
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [

            ${items.map(item => {
        return `{
                "@type": "Question",
                "name": ${JSON.stringify(get(item, 'title.field.value'))},
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": ${JSON.stringify(get(item, 'description.field.value'))}
                }
              }`;
      })}]
          }`
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "row m01-accordion-faq"
  }, /*#__PURE__*/React.createElement("div", {
    className: "section-faq",
    "data-cy": "accordion-faq"
  }, titleField && titleFieldValue && titleFieldValue.trim() && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "accordion-main-heading heading-w-link"
  }, /*#__PURE__*/React.createElement("div", {
    className: "titleDescriptionWrapper"
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "div",
    className: "step-title",
    field: titleField,
    "data-cy": "accordion-faq-title"
  }), description ? /*#__PURE__*/React.createElement(RichText, {
    className: "accordionDescription",
    tag: "div",
    field: {
      value: description
    }
  }) : null), !pageEditing && collapseAllText && expandAllText && /*#__PURE__*/React.createElement("div", {
    className: "expand-wrapper"
  }, /*#__PURE__*/React.createElement("a", {
    className: "expand-accordion-btn default-link",
    href: "/",
    onClick: handleToggleAllItemsClick,
    "data-cy": "accordion-steps-expand-all"
  }, allItemsExpanded ? collapseAllText : expandAllText), isDetSite ? /*#__PURE__*/React.createElement("div", {
    className: "iconBlock",
    style: {
      lineHeight: '0'
    }
  }, allItemsExpanded ? /*#__PURE__*/React.createElement(IconMinuss, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(IconExpandPlus, {
    className: "icon"
  })) : null))), /*#__PURE__*/React.createElement("div", {
    className: `accordion accordion--faq ${oneColumn ? 'accordion--one-column' : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "accordion__col accordion__col--left"
  }, items.map((item, index) => {
    if (index < middleArrIndex) {
      if (item.children && item.children.length > 0) {
        const description = get(item, 'description.jss.value') || '';
        return /*#__PURE__*/React.createElement("div", {
          className: "accordion-faq-category"
        }, description && /*#__PURE__*/React.createElement("span", {
          className: "label-small"
        }, description), item.children.map(el => renderItem(el, pageEditing, isDetSite)));
      } else {
        return renderItem(item, pageEditing, isDetSite);
      }
    }
    return null;
  })), /*#__PURE__*/React.createElement("div", {
    className: "accordion__col accordion__col--right"
  }, items.map((item, index) => {
    const description = get(item, 'description.jss.value') || '';
    if (index >= middleArrIndex) {
      if (item.children && item.children.length > 0) {
        return /*#__PURE__*/React.createElement("div", {
          className: "accordion-faq-category"
        }, description && /*#__PURE__*/React.createElement("span", {
          className: "label-small"
        }, description), item.children.map(el => renderItem(el, pageEditing, isDetSite)));
      } else {
        return renderItem(item, pageEditing, isDetSite);
      }
    }
    return null;
  }))), isDetSite && /*#__PURE__*/React.createElement(RouterLink, {
    className: "btn btn-primary accordion__readmore",
    href: readAllLinkHref,
    onClick: () => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['readAllAnalytics'], params)),
    dataCy: "accordion-faq-read-more",
    title: readAllLabel
  }, ctaLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: ctaLeftIcon.svg.jss.value
    }
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "btn-link__text"
  }, readAllLabel), ctaRightIcon ? /*#__PURE__*/React.createElement("span", {
    className: "iconRight",
    dangerouslySetInnerHTML: {
      __html: ctaRightIcon.svg.jss.value
    }
  }) : null), !isDetSite && (pageEditing ? /*#__PURE__*/React.createElement(SitecoreLink, {
    className: "btn btn-link accordion__readmore",
    field: readAllLinkField,
    title: readAllLabel ? readAllLabel : '[Link]'
  }, ctaLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: ctaLeftIcon.svg.jss.value
    }
  }) : null, /*#__PURE__*/React.createElement(Text, {
    field: readAllLabelField,
    tag: "span",
    className: "btn-link__text"
  }), ctaRightIcon ? /*#__PURE__*/React.createElement("span", {
    className: "iconRight",
    dangerouslySetInnerHTML: {
      __html: ctaRightIcon.svg.jss.value
    }
  }) : null) : readAllLabel && readAllLinkHref && readAllLinkHref !== '/' && /*#__PURE__*/React.createElement(RouterLink, {
    className: "btn btn-link accordion__readmore",
    href: readAllLinkHref,
    onClick: () => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['readAllAnalytics'], params)),
    dataCy: "accordion-faq-read-more",
    title: readAllLabel
  }, ctaLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: ctaLeftIcon.svg.jss.value
    }
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "btn-link__text"
  }, readAllLabel), ctaRightIcon ? /*#__PURE__*/React.createElement("span", {
    className: "iconRight",
    dangerouslySetInnerHTML: {
      __html: ctaRightIcon.svg.jss.value
    }
  }) : null)))));
};
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    isDetSite,
    anchorUrlParameter
  } = _ref2;
  const commonStyles = isDetSite ? "cb58dqw" : "c14ytd6h";
  const ltrStyles = isDetSite ? "l1jxjn18" : "layady6";
  const rtlStyles = isDetSite ? "r71crbd" : "r1k86pzj";
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    anchorUrlParameter: anchorUrlParameter,
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
export default AccordionFAQ;